import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { MapPin, Truck, ShieldCheck, CreditCard, Instagram, ArrowRight } from "lucide-react";

const BENEFITS = [
  { icon: Truck, title: "Retirada grátis", desc: "Retire na nossa loja em Magé-RJ" },
  { icon: CreditCard, title: "Parcelamento", desc: "Em até 3x sem juros no cartão" },
  { icon: ShieldCheck, title: "Compra segura", desc: "Pagamento protegido por Pix ou cartão" },
  { icon: MapPin, title: "Loja física", desc: "Atendimento próximo e personalizado" },
];

const CATEGORY_TILES = [
  { name: "Feminino", image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&q=80" },
  { name: "Masculino", image: "https://images.unsplash.com/photo-1516257984-b1b4d707412e?w=600&q=80" },
  { name: "Infantil", image: "https://images.unsplash.com/photo-1519238263530-99bdd11df2ea?w=600&q=80" },
  { name: "Acessórios", image: "https://images.unsplash.com/photo-1523779917675-b6ed3a42a561?w=600&q=80" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [onSale, setOnSale] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(Array.isArray(r.data) ? r.data : (r.data?.products || [])));
api.get("/products?is_new=true&limit=8").then((r) => setNewArrivals(Array.isArray(r.data) ? r.data : (r.data?.products || [])));
api.get("/products?on_sale=true&limit=8").then((r) => setOnSale(Array.isArray(r.data) ? r.data : (r.data?.products || [])));
  api.get("/settings").then((r) => setSettings(r.data || {}));
}, []);

  return (
    <div>
      {/* HERO */}
<section className="relative bg-gradient-to-br from-[#F7F5F2] to-white overflow-hidden">
  <div className="max-w-7xl mx-auto px-4 py-10 sm:py-16 lg:py-20 grid lg:grid-cols-12 gap-8 items-center">
    <div className="lg:col-span-6 space-y-5 order-2 lg:order-1">
      
      <span className="inline-block text-xs uppercase font-bold tracking-[0.2em] text-[#C91F1F]">
        {settings.hero_eyebrow || "Nova coleção • Verão 2026"}
      </span>

      <h1
        className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-neutral-900"
        style={{ fontFamily: "Outfit" }}
      >
        {settings.hero_headline || "Moda para todos os estilos"}
      </h1>

      <p className="text-base sm:text-lg text-neutral-600 max-w-lg leading-relaxed">
        {settings.hero_support ||
          "Variedade, estilo e preço para você. Feminino, masculino, infantil e acessórios — direto da loja para o seu guarda-roupa."}
      </p>

      <div className="flex flex-wrap gap-3 pt-2">
        <Link to="/categoria/Novidades">
          <Button
            data-testid="hero-cta-primary"
            className="bg-[#C91F1F] hover:bg-[#9F1515] h-12 px-6 font-semibold"
          >
            Comprar agora <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>

        <Link to="/categoria/Promoções">
          <Button
            data-testid="hero-cta-secondary"
            variant="outline"
            className="h-12 px-6 font-semibold border-neutral-900"
          >
            Ver ofertas
          </Button>
        </Link>
      </div>

      <div className="flex items-center gap-3 pt-3 text-xs text-neutral-500">
        <span className="bg-[#D4A928]/20 text-[#8B7013] px-2 py-1 rounded-md font-semibold">
          {settings.hero_badge || "ATACADO & VAREJO"}
        </span>
        <span>{settings.hero_badge_support || "Preço único • Retirada em Magé-RJ"}</span>
      </div>
    </div>

    <div className="lg:col-span-6 order-1 lg:order-2">
      <div className="relative aspect-[4/5] sm:aspect-[16/10] lg:aspect-[4/5] rounded-2xl overflow-hidden">
        <img
          src={settings.hero_image || "https://images.unsplash.com/photo-1694452243147-6282e82b2b4a?w=1200&q=85"}
          alt="Coleção Nalin"
          className="w-full h-full object-cover"
        />

        <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
          <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">
            {settings.hero_card_label || "Destaque da semana"}
          </p>
          <p className="text-base font-bold text-neutral-900">
            {settings.hero_card_title || "Coleção Verão em pré-venda"}
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 py-10 sm:py-14">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Categorias em destaque</h2>
          <Link to="/categorias" className="text-sm font-semibold text-[#C91F1F] hover:underline">Ver todas</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {CATEGORY_TILES.map((c) => (
            <Link key={c.name} to={`/categoria/${encodeURIComponent(c.name)}`} className="group block relative aspect-square rounded-xl overflow-hidden">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white font-bold text-sm sm:text-base">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* NEW ARRIVALS */}
      {newArrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-6 sm:py-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="text-xs uppercase font-bold tracking-widest text-[#C91F1F]">Recém-chegados</span>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Novidades da semana</h2>
            </div>
            <Link to="/categoria/Novidades" className="text-sm font-semibold text-[#C91F1F] hover:underline">Ver mais</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {newArrivals.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* PROMO BANNER */}
      <section className="max-w-7xl mx-auto px-4 py-6">
        <div className="relative bg-[#181818] rounded-2xl overflow-hidden p-8 sm:p-12 grid sm:grid-cols-2 items-center gap-6">
          <div>
            <span className="text-[#D4A928] text-xs font-bold uppercase tracking-widest">{settings.promo_eyebrow || "Ofertas exclusivas"}</span>
            <h3 className="text-white text-3xl sm:text-4xl font-extrabold mt-2 leading-tight" style={{ fontFamily: "Outfit" }}>
              {settings.promo_title || "Até 30% OFF em peças selecionadas"}
            </h3>
            <p className="text-neutral-300 mt-3 text-sm sm:text-base">
  {settings.promo_banner || "Confira nossas ofertas e condições especiais."}
</p>
            <Link to="/categoria/Promoções">
              <Button className="mt-5 bg-[#C91F1F] hover:bg-[#9F1515] h-11 px-6">Aproveitar ofertas</Button>
            </Link>
          </div>
          <div className="hidden sm:block aspect-[4/3] rounded-xl overflow-hidden">
            <img src={settings.promo_image || "https://images.unsplash.com/photo-1513441863195-bd50bb875298?w=800&q=85"} alt="Ofertas" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-10 sm:py-14">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Mais desejados</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* BENEFITS */}
      <section className="bg-[#F7F5F2] py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white rounded-xl p-4 sm:p-5 flex items-start gap-3">
              <div className="bg-[#C91F1F]/10 text-[#C91F1F] p-2.5 rounded-lg"><Icon className="h-5 w-5" /></div>
              <div>
                <h4 className="font-semibold text-sm sm:text-base">{title}</h4>
                <p className="text-xs text-neutral-500 mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* STORE SECTION */}
      <section className="max-w-7xl mx-auto px-4 py-10 sm:py-14 grid lg:grid-cols-2 gap-8 items-center">
        <div className="aspect-[4/3] rounded-2xl overflow-hidden">
          <img src={settings.store_image || "https://images.unsplash.com/photo-1603400521630-9f2de124b33b?w=1000&q=85"} alt="Loja Nalin" className="w-full h-full object-cover" />
        </div>
        <div className="space-y-4">
          <span className="text-xs uppercase font-bold tracking-widest text-[#C91F1F]">{settings.store_section_label || "Loja física"}</span>
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>{settings.store_section_title || "Conheça nossa loja em Magé"}</h2>
          <p className="text-neutral-600 leading-relaxed">
            Uma seleção completa de peças em nossa loja física, com atendimento próximo e personalizado.
            Compre online e retire grátis quando estiver pronto.
          </p>
          <div className="flex items-center gap-3 text-sm">
            <MapPin className="h-4 w-4 text-[#C91F1F]" />
            <span>{settings.address || "Rua Dr. Siqueira, nº 269, Magé - RJ"}</span>
          </div>
          <div className="flex gap-3">
            <a href="https://www.google.com/maps/search/?api=1&query=Rua+Dr.+Siqueira+269+Magé+RJ" target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-neutral-900">Como chegar</Button>
            </a>
            <a
 href={`https://wa.me/${(settings.whatsapp || "5521984953716").replace(/\D/g, "")}`}
  target="_blank"
  rel="noreferrer"
>
              <Button className="bg-[#25D366] hover:bg-[#1ea952]">WhatsApp</Button>
            </a>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-7xl mx-auto px-4 py-8 sm:py-10 text-center">
        <Instagram className="h-6 w-6 mx-auto text-[#C91F1F]" />
        <h3 className="text-2xl font-bold mt-2" style={{ fontFamily: "Outfit" }}>
  Siga @{(settings.instagram || "nalinatacado").replace(/^@/, "").replace(/^https?:\/\/(www\.)?instagram\.com\//, "").replace(/\/$/, "")}
</h3>
<p className="text-sm text-neutral-500 mt-1">
  Novidades, ofertas e bastidores todo dia no nosso Instagram.
</p>
<a
  href={
    (settings.instagram || "").startsWith("http")
      ? settings.instagram
      : `https://instagram.com/${(settings.instagram || "nalinatacado").replace(/^@/, "")}`
  }
  target="_blank"
  rel="noreferrer"
>
          <Button variant="outline" className="mt-4 border-neutral-900">Ver Instagram</Button>
        </a>
      </section>
      <section className="max-w-7xl mx-auto px-4 pb-8 text-center">
  <a
    href={settings.facebook || "https://www.facebook.com/nalinatacado"}
    target="_blank"
    rel="noreferrer"
  >
    <Button variant="outline" className="border-neutral-900">
      Ver Facebook
    </Button>
  </a>
</section>
    </div>
  );
}
